<script setup lang="ts">
import { computed } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import {
  hiddenTools,
  isGroupVisible,
  setGroupVisible,
  setToolVisible,
} from "@/stores/ToolbarVisibilityState";
import {
  TOOLBAR_TOOL_ICONS,
  type ToolbarGroupId,
  type ToolbarToolId,
} from "@/utils/toolbar-tools";

/**
 * Un groupe d'outils de la modale Affichage : l'interrupteur affiche ou masque
 * tout le groupe, les cases outil par outil.
 */
const props = defineProps<{
  groupId: ToolbarGroupId;
  tools: readonly ToolbarToolId[];
}>();

const groupVisible = computed({
  get: () => isGroupVisible(props.groupId),
  set: (visible: boolean) => setGroupVisible(props.groupId, visible),
});

const isVisible = (tool: ToolbarToolId) => !hiddenTools.value.includes(tool);
</script>

<template>
  <div class="display-tool-group">
    <div class="display-tool-group-header">
      <span class="display-tool-group-name">{{
        $t(`canvas:display.groups.${groupId}`)
      }}</span>
      <el-switch
        v-model="groupVisible"
        class="display-switch"
        :aria-label="
          $t('canvas:display.groupToggle', {
            group: $t(`canvas:display.groups.${groupId}`),
          })
        "
      />
    </div>
    <div class="display-tool-list">
      <el-checkbox
        v-for="tool in tools"
        :key="tool"
        :model-value="isVisible(tool)"
        class="display-checkbox"
        @update:model-value="(checked) => setToolVisible(tool, !!checked)"
      >
        <span class="d-flex align-items-center gap-1">
          <PhosphorIcon :name="TOOLBAR_TOOL_ICONS[tool]" :size="16" />
          {{ $t(`canvas:display.tools.${tool}`) }}
        </span>
      </el-checkbox>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.display-tool-group + .display-tool-group {
  margin-top: 14px;
}

.display-tool-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 4px;
}

.display-tool-group-name {
  font-size: 12.5px;
  font-weight: 600;
}

.display-tool-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  padding: 4px 8px 6px;
}
</style>
